/**
 * Route → help-article slug mapping.
 *
 * Pure function: takes a `pathname` and returns the slug of the help article
 * relevant to that page, or `null` if no article is registered for the route.
 *
 * Order matters: more-specific patterns are checked before less-specific ones,
 * so `/invoices/review` resolves to `review-queue` rather than the dynamic
 * `/invoices/<id>` → `invoices-detail` mapping.
 *
 * This module lives in `packages/shared` so Phase 7B can reuse the same
 * lookup server-side once articles are DB-backed. The Phase 7A registry in
 * `apps/web/src/help/registry.ts` resolves the slug → article body.
 */

export const HELP_SLUGS = [
	"overview",
	"billing-cycle",
	"attention",
	"review-queue",
	"documents",
	"documents-detail",
	"customers",
	"customers-detail",
	"invoices",
	"invoices-detail",
	"invoices-generate",
	"reports",
	"settings-profile",
	"settings-security",
	"settings-notifications",
	"settings-team",
	"settings-quickbooks",
	"settings-customer-mapping",
	"settings-invoicing",
	"settings-danger-zone",
] as const;

export type HelpSlug = (typeof HELP_SLUGS)[number];

/**
 * Resolve a route pathname to a help-article slug.
 *
 * Returns `null` for any route not explicitly registered — the caller (the
 * help dialog's empty state) renders "Browse all topics" in that case.
 *
 * Trailing slashes are tolerated by stripping them once before matching, so
 * `/customers/` resolves the same as `/customers`. Query strings and hashes
 * are NOT stripped — pass `location.pathname`, not `location.href`.
 */
export function getHelpSlugForRoute(pathname: string): HelpSlug | null {
	// Tolerate a single trailing slash on otherwise-valid paths. Don't touch
	// the root "/" which would collapse to empty.
	const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

	// Exact matches first — first match wins, so /invoices/review and
	// /invoices/generate are caught before the dynamic /invoices/<id> branch.
	if (path === "/") return "overview";
	if (path === "/cycle") return "billing-cycle";
	if (path === "/attention") return "attention";
	if (path === "/invoices/review") return "review-queue";
	if (path === "/invoices/generate") return "invoices-generate";
	if (path === "/invoices") return "invoices";
	if (path === "/documents") return "documents";
	if (path === "/customers") return "customers";
	if (path === "/reports") return "reports";

	// Settings: the /settings index redirects to /settings/profile, so the
	// landing page article is the same as the profile article. Each
	// sub-page has its own article since they're functionally distinct
	// surfaces (QBO connection vs. team management vs. danger zone, etc.).
	if (path === "/settings" || path === "/settings/profile") return "settings-profile";
	if (path === "/settings/security") return "settings-security";
	if (path === "/settings/notifications") return "settings-notifications";
	if (path === "/settings/team") return "settings-team";
	if (path === "/settings/quickbooks") return "settings-quickbooks";
	if (path === "/settings/customer-mapping") return "settings-customer-mapping";
	if (path === "/settings/invoicing") return "settings-invoicing";
	if (path === "/settings/danger-zone") return "settings-danger-zone";

	// Dynamic detail routes: /<resource>/<id> with exactly one path segment
	// after the resource name. Avoid greedy matches that would swallow
	// /invoices/review (handled above) or deeper nested routes.
	if (/^\/invoices\/[^/]+$/.test(path)) return "invoices-detail";
	if (/^\/documents\/[^/]+$/.test(path)) return "documents-detail";
	if (/^\/customers\/[^/]+$/.test(path)) return "customers-detail";

	return null;
}
